import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import PlanDialog from '@/components/PlanDialog';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { downloadFile } from '@/lib/download';
import {
  RECURRING_TYPES, BADGE_STYLE, STATE_STYLE, currentMonth, localISODate, shiftMonth, monthLabel, money, fmtDate, typeLabel,
} from '@/lib/payments';
import { toast } from 'sonner';
import {
  CalendarClock, ChevronLeft, ChevronRight, Search, CheckCircle2, Undo2, Pencil, Printer, Download,
  Plus, Ban, RotateCcw, Trash2, Wand2, StopCircle, Play,
} from 'lucide-react';

const API = `${(window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || '')}/api`;
const today = () => localISODate();
const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

const StatePill = ({ row }) => {
  const st = STATE_STYLE[row.state] || STATE_STYLE.pending;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${st.cls}`} data-testid={`state-${row.id}`}>
      {st.label}{row.state === 'paid' && row.paid_date ? ` ${fmtDate(row.paid_date).slice(0, 5)}` : ''}
    </span>
  );
};

/* ───────────────────────── Pagos del mes ───────────────────────── */

const MonthTab = () => {
  const [month, setMonth] = useState(currentMonth());
  const [status, setStatus] = useState('all');
  const [type, setType] = useState('all');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [includePrev, setIncludePrev] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [payDate, setPayDate] = useState(today());
  const [busy, setBusy] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editAmount, setEditAmount] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const params = useMemo(() => ({
    month, status, type: type === 'all' ? undefined : type, search: debounced || undefined,
    include_previous_pending: includePrev,
  }), [month, status, type, debounced, includePrev]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/recurring/charges`, { params });
      setData(res.data);
      setSelected(new Set());
    } catch (e) {
      toast.error(errMsg(e, 'No se pudieron cargar los pagos'));
    } finally {
      setLoading(false);
    }
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const summary = data?.summary;
  const selectedRows = rows.filter((r) => selected.has(r.id));
  const selPending = selectedRows.filter((r) => r.status === 'pending');
  const selPaid = selectedRows.filter((r) => r.status === 'paid');
  const selTotal = selPending.reduce((a, r) => a + r.amount, 0);
  const allPendingIds = rows.filter((r) => r.status === 'pending').map((r) => r.id);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected((prev) => (prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))));

  const post = async (path, body, okMsg) => {
    try {
      setBusy(true);
      const res = await axios.post(`${API}/recurring/charges/${path}`, body);
      if (okMsg) toast.success(okMsg(res.data));
      await load();
      return res.data;
    } catch (e) {
      toast.error(errMsg(e, 'La operación falló'));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const markPaid = (ids) => post('mark-paid', { ids, paid_date: payDate }, (r) =>
    `${r.paid} pago(s) marcados como pagados por ${money(r.total)}${r.skipped ? ` (${r.skipped} ya estaban pagados)` : ''}`);
  const markUnpaid = (ids) => post('mark-unpaid', { ids }, (r) => `${r.reverted} pago(s) desmarcados`);
  const cancel = (ids) => post('cancel', { ids }, (r) => `${r.cancelled} cuota(s) anuladas`);
  const restore = (ids) => post('restore', { ids }, (r) => `${r.restored} cuota(s) restauradas`);

  const saveEdit = async () => {
    const v = parseFloat(editAmount);
    if (!v || v <= 0) return toast.error('Ingresá un monto mayor a 0');
    try {
      await axios.put(`${API}/recurring/charges/${editRow.id}`, { amount: v });
      toast.success('Monto actualizado');
      setEditRow(null);
      load();
    } catch (e) {
      toast.error(errMsg(e, 'No se pudo guardar'));
    }
  };

  const exportParams = { ...params, include_previous_pending: includePrev };
  const printList = async () => {
    // Se abre la ventana antes de pedir el archivo para que el navegador no la bloquee
    const w = window.open('about:blank', '_blank');
    try {
      const res = await axios.get(`${API}/recurring/export/pdf`, { params: exportParams, responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      if (w) { w.location.href = url; } else { window.location.href = url; }
    } catch (e) {
      if (w) w.close();
      toast.error('No se pudo generar el listado');
    }
  };

  return (
    <div className="space-y-4">
      {/* Mes + exportes */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="month-prev" aria-label="Mes anterior">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="min-w-[10rem] text-center text-lg font-semibold text-gray-900" data-testid="month-label">{monthLabel(month)}</div>
          <Button variant="outline" size="icon" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="month-next" aria-label="Mes siguiente">
            <ChevronRight className="w-4 h-4" />
          </Button>
          {month !== currentMonth() && (
            <Button variant="ghost" size="sm" onClick={() => setMonth(currentMonth())}>Mes actual</Button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={printList} data-testid="btn-print">
            <Printer className="w-4 h-4 mr-2" /> Imprimir listado
          </Button>
          <Button variant="outline" onClick={() => downloadFile(`${API}/recurring/export/xlsx`, { params: exportParams, fallbackName: `pagos_mensuales_${month}.xlsx` })} data-testid="btn-excel">
            <Download className="w-4 h-4 mr-2" /> Excel
          </Button>
        </div>
      </div>

      {/* Resumen */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="summary">
          <Card className="p-3"><p className="text-xs text-gray-500">Pendiente</p><p className="text-xl font-bold text-amber-700" data-testid="sum-pending">{money(summary.pending_total)}</p><p className="text-xs text-gray-500">{summary.pending_count} pago(s)</p></Card>
          <Card className="p-3"><p className="text-xs text-gray-500">Pagado</p><p className="text-xl font-bold text-emerald-700" data-testid="sum-paid">{money(summary.paid_total)}</p><p className="text-xs text-gray-500">{summary.paid_count} pago(s)</p></Card>
          <Card className="p-3"><p className="text-xs text-gray-500">Atrasados</p><p className={`text-xl font-bold ${summary.overdue_count ? 'text-red-700' : 'text-gray-400'}`} data-testid="sum-overdue">{summary.overdue_count}</p><p className="text-xs text-gray-500">sin marcar como pagados</p></Card>
          <Card className="p-3"><p className="text-xs text-gray-500">Total</p><p className="text-xl font-bold text-gray-900">{money(summary.total_amount)}</p><p className="text-xs text-gray-500">{summary.total_count} pago(s)</p></Card>
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-col md:flex-row gap-2 md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Buscar jugador..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full md:w-44" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Pendientes y pagados</SelectItem>
            <SelectItem value="pending">Solo pendientes</SelectItem>
            <SelectItem value="paid">Solo pagados</SelectItem>
            <SelectItem value="cancelled">Anulados</SelectItem>
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-full md:w-44" data-testid="filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Viáticos y canastas</SelectItem>
            {RECURRING_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap">
          <input type="checkbox" checked={includePrev} onChange={(e) => setIncludePrev(e.target.checked)} data-testid="include-prev" />
          Incluir atrasados de meses anteriores
        </label>
      </div>

      {/* Barra de acciones en lote */}
      <Card className={`p-3 sticky top-0 z-10 border-brand-200 ${selected.size ? 'bg-brand-50' : 'bg-gray-50'}`} data-testid="bulk-bar">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="text-sm text-gray-700 flex-1">
            {selected.size ? (
              <span><strong>{selected.size}</strong> seleccionado(s){selPending.length ? ` · ${selPending.length} pendiente(s) por ${money(selTotal)}` : ''}</span>
            ) : (
              <span>Tildá los pagos que se hicieron y marcalos todos juntos.</span>
            )}
            {allPendingIds.length > 0 && (
              <button type="button" className="ml-3 text-brand-700 underline text-sm" onClick={() => setSelected(new Set(allPendingIds))} data-testid="select-pending">
                Seleccionar todos los pendientes ({allPendingIds.length})
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs whitespace-nowrap">Fecha de pago</Label>
            <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className="w-40 h-9" data-testid="pay-date" />
            <Button disabled={busy || selPending.length === 0} onClick={() => markPaid(selPending.map((r) => r.id))}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-mark-paid">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Marcar pagados ({selPending.length})
            </Button>
            {selPaid.length > 0 && (
              <Button variant="outline" disabled={busy} onClick={() => markUnpaid(selPaid.map((r) => r.id))} data-testid="btn-mark-unpaid">
                <Undo2 className="w-4 h-4 mr-2" /> Deshacer ({selPaid.length})
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Tabla */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>
      ) : rows.length === 0 ? (
        <Card className="p-10 text-center text-gray-500" data-testid="empty">
          <CalendarClock className="w-10 h-10 mx-auto text-gray-300 mb-2" />
          No hay pagos para mostrar en {monthLabel(month)}.
          <p className="text-xs mt-1">Los pagos aparecen solos cuando un jugador tiene un plan mensual (pestaña "Planes").</p>
        </Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="charges-table">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2 w-10"><input type="checkbox" checked={selected.size === rows.length && rows.length > 0} onChange={toggleAll} data-testid="check-all" aria-label="Seleccionar todos" /></th>
                <th className="px-3 py-2 text-left">Jugador</th>
                <th className="px-3 py-2 text-left">Concepto</th>
                <th className="px-3 py-2 text-left">Mes</th>
                <th className="px-3 py-2 text-right">Monto</th>
                <th className="px-3 py-2 text-left">Estado</th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((r) => (
                <tr key={r.id} className={`${selected.has(r.id) ? 'bg-brand-50/60' : r.state === 'overdue' ? 'bg-red-50/40' : ''}`} data-testid={`row-${r.id}`}>
                  <td className="px-3 py-2"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} data-testid={`check-${r.id}`} aria-label={`Seleccionar ${r.player_name}`} /></td>
                  <td className="px-3 py-2 font-medium"><Link to={`/players/${r.player_id}`} className="hover:text-brand-600">{r.player_name}</Link>{r.player_status === 'inactive' && <span className="ml-2 text-xs font-normal text-gray-500">(inactivo)</span>}</td>
                  <td className="px-3 py-2 text-gray-700">{r.type_label}</td>
                  <td className="px-3 py-2 text-gray-700">{r.month_label}</td>
                  <td className="px-3 py-2 text-right font-semibold">{money(r.amount)}</td>
                  <td className="px-3 py-2"><StatePill row={r} /></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {r.status === 'pending' && (
                      <>
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => markPaid([r.id])} className="mr-1 text-emerald-700 border-emerald-300" data-testid={`pay-${r.id}`}>Pagar</Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" title="Editar monto de esta cuota" onClick={() => { setEditRow(r); setEditAmount(String(r.amount)); }} data-testid={`edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-gray-500" title="Anular esta cuota" onClick={() => cancel([r.id])} data-testid={`cancel-${r.id}`}><Ban className="w-4 h-4" /></Button>
                      </>
                    )}
                    {r.status === 'paid' && (
                      <Button size="sm" variant="ghost" disabled={busy} onClick={() => markUnpaid([r.id])} data-testid={`undo-${r.id}`}><Undo2 className="w-4 h-4 mr-1" /> Deshacer</Button>
                    )}
                    {r.status === 'cancelled' && (
                      <Button size="sm" variant="ghost" disabled={busy} onClick={() => restore([r.id])} data-testid={`restore-${r.id}`}><RotateCcw className="w-4 h-4 mr-1" /> Restaurar</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Editar monto</DialogTitle>
            <DialogDescription>{editRow?.player_name} · {editRow?.type_label} · {editRow?.month_label}. Solo cambia esta cuota.</DialogDescription>
          </DialogHeader>
          <Input type="number" min="0" step="0.01" value={editAmount} onChange={(e) => setEditAmount(e.target.value)} data-testid="edit-amount" />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setEditRow(null)}>Cancelar</Button>
            <Button onClick={saveEdit} className="bg-brand-600 hover:bg-brand-700" data-testid="edit-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ───────────────────────── Planes ───────────────────────── */

const PlansTab = ({ initialPlayer }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyCurrent, setOnlyCurrent] = useState(true);
  const [type, setType] = useState('all');
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState({ open: false, plan: null });
  const [migration, setMigration] = useState(null); // { open, data }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/recurring/plans`, {
        params: { only_current: onlyCurrent, type: type === 'all' ? undefined : type, search: search.trim() || undefined },
      });
      setPlans(res.data || []);
    } catch (e) {
      toast.error(errMsg(e, 'No se pudieron cargar los planes'));
    } finally {
      setLoading(false);
    }
  }, [onlyCurrent, type, search]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => { if (initialPlayer) setDialog({ open: true, plan: null }); }, [initialPlayer]);

  const act = async (fn, okMsg) => {
    try {
      setBusy(true);
      await fn();
      if (okMsg) toast.success(okMsg);
      load();
    } catch (e) {
      toast.error(errMsg(e, 'La operación falló'));
    } finally {
      setBusy(false);
    }
  };

  const openMigration = async () => {
    try {
      const res = await axios.post(`${API}/recurring/migrate-existing`, null, { params: { dry_run: true } });
      setMigration({ open: true, data: res.data });
    } catch (e) {
      toast.error(errMsg(e, 'No se pudo preparar la conversión'));
    }
  };
  const runMigration = async () => {
    await act(async () => {
      const res = await axios.post(`${API}/recurring/migrate-existing`, null, { params: { dry_run: false } });
      toast.success(`Se crearon ${res.data.created} plan(es) mensual(es)`);
      setMigration(null);
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-2 md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Buscar jugador..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="plans-search" />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-full md:w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Viáticos y canastas</SelectItem>
            {RECURRING_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap">
          <input type="checkbox" checked={!onlyCurrent} onChange={(e) => setOnlyCurrent(!e.target.checked)} data-testid="show-finished" />
          Mostrar finalizados
        </label>
        <Button variant="outline" onClick={openMigration} data-testid="btn-migrate"><Wand2 className="w-4 h-4 mr-2" /> Convertir los ya cargados</Button>
        <Button onClick={() => setDialog({ open: true, plan: null })} className="bg-brand-600 hover:bg-brand-700" data-testid="btn-new-plan"><Plus className="w-4 h-4 mr-2" /> Nuevo plan</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>
      ) : plans.length === 0 ? (
        <Card className="p-10 text-center text-gray-500" data-testid="plans-empty">
          No hay planes mensuales. Creá uno con "Nuevo plan" o convertí los viáticos y canastas que ya estaban cargados.
        </Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="plans-table">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Jugador</th>
                <th className="px-3 py-2 text-left">Concepto</th>
                <th className="px-3 py-2 text-right">Monto mensual</th>
                <th className="px-3 py-2 text-left">Desde</th>
                <th className="px-3 py-2 text-left">Hasta</th>
                <th className="px-3 py-2 text-left">Estado</th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {plans.map((p) => {
                const b = BADGE_STYLE[p.badge] || BADGE_STYLE.al_dia;
                return (
                  <tr key={p.id} className={p.is_current ? '' : 'opacity-60'} data-testid={`plan-${p.id}`}>
                    <td className="px-3 py-2 font-medium"><Link to={`/players/${p.player_id}`} className="hover:text-brand-600">{p.player_name}</Link>{p.player_status === 'inactive' && <span className="ml-2 text-xs text-gray-500">(inactivo)</span>}</td>
                    <td className="px-3 py-2">{p.type_label}</td>
                    <td className="px-3 py-2 text-right font-semibold">{money(p.amount)}</td>
                    <td className="px-3 py-2">{monthLabel(p.start_month)}</td>
                    <td className="px-3 py-2">{p.end_month ? monthLabel(p.end_month) : <span className="text-gray-400">Sin fin</span>}</td>
                    <td className="px-3 py-2">
                      {p.is_current ? (
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${b.cls}`} data-testid={`plan-badge-${p.id}`}>{b.label}</span>
                      ) : <span className="text-xs text-gray-500">Finalizado</span>}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" className="h-8 w-8" title="Editar monto" onClick={() => setDialog({ open: true, plan: p })} data-testid={`plan-edit-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                      {p.is_current ? (
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-amber-700" title="Finalizar: dejar de cobrar desde este mes" disabled={busy}
                          onClick={() => window.confirm(`¿Dejar de cobrar ${p.type_label.toLowerCase()} a ${p.player_name} desde este mes? La cuota pendiente de este mes se anula.`) && act(() => axios.put(`${API}/recurring/plans/${p.id}`, { finish: true }), 'Plan finalizado')} data-testid={`plan-finish-${p.id}`}><StopCircle className="w-4 h-4" /></Button>
                      ) : (
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-700" title="Reanudar desde este mes" disabled={busy}
                          onClick={() => act(() => axios.put(`${API}/recurring/plans/${p.id}`, { resume: true }), 'Plan reanudado')} data-testid={`plan-resume-${p.id}`}><Play className="w-4 h-4" /></Button>
                      )}
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" title="Eliminar (solo si no tiene pagos)" disabled={busy}
                        onClick={() => window.confirm(`¿Eliminar el plan de ${p.type_label.toLowerCase()} de ${p.player_name}?`) && act(() => axios.delete(`${API}/recurring/plans/${p.id}`), 'Plan eliminado')} data-testid={`plan-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <PlanDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
        plan={dialog.plan}
        initialPlayer={initialPlayer}
        onSaved={load}
      />

      <Dialog open={!!migration} onOpenChange={(o) => !o && setMigration(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="migration-dialog">
          <DialogHeader>
            <DialogTitle>Convertir viáticos y canastas ya cargados</DialogTitle>
            <DialogDescription>
              Cada jugador con viáticos o canasta cargados pasa a tener un pago mensual con el monto de la última carga.
              El historial no se toca. Empieza este mes (o el que viene si ya se pagó uno este mes).
            </DialogDescription>
          </DialogHeader>
          {migration?.data?.count === 0 ? (
            <p className="text-sm text-gray-600 py-4" data-testid="migration-empty">No hay nada para convertir: los jugadores con viáticos o canasta ya tienen su plan mensual.</p>
          ) : (
            <table className="w-full text-sm" data-testid="migration-table">
              <thead className="text-xs uppercase text-gray-500 border-b">
                <tr><th className="text-left py-1">Jugador</th><th className="text-left">Concepto</th><th className="text-right">Monto mensual</th><th className="text-left pl-3">Empieza</th><th className="text-left pl-3">Última carga</th></tr>
              </thead>
              <tbody className="divide-y">
                {(migration?.data?.proposals || []).map((p) => (
                  <tr key={`${p.player_id}-${p.type}`}>
                    <td className="py-1.5 font-medium">{p.player_name}</td><td>{typeLabel(p.type)}</td>
                    <td className="text-right">{money(p.amount)}</td><td className="pl-3">{monthLabel(p.start_month)}</td><td className="pl-3 text-gray-500">{fmtDate(p.last_investment_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setMigration(null)}>Cerrar</Button>
            {migration?.data?.count > 0 && (
              <Button onClick={runMigration} disabled={busy} className="bg-brand-600 hover:bg-brand-700" data-testid="migration-confirm">
                Crear {migration.data.count} plan(es) mensual(es)
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ───────────────────────── Página ───────────────────────── */

const Payments = () => {
  usePageTitle('Pagos mensuales');
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') === 'planes' ? 'planes' : 'mes');
  const initialPlayer = useMemo(() => {
    const id = searchParams.get('player');
    const name = searchParams.get('name');
    return id ? { id, name: name || 'Jugador' } : null;
  }, [searchParams]);

  return (
    <Layout>
      <div className="space-y-4 max-w-6xl mx-auto" data-testid="payments-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarClock className="w-7 h-7 text-brand-600" /> Pagos mensuales
          </h1>
          <p className="text-sm text-gray-500 mt-1">Viáticos y canastas de alimentos: se generan solos cada mes y se marcan cuando se pagan (alrededor del día 20).</p>
        </div>
        <div className="inline-flex rounded-lg bg-gray-100 p-1" role="tablist">
          {[['mes', 'Pagos del mes'], ['planes', 'Planes']].map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === k ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}>
              {label}
            </button>
          ))}
        </div>
        {tab === 'mes' ? <MonthTab /> : <PlansTab initialPlayer={initialPlayer} />}
      </div>
    </Layout>
  );
};

export default Payments;
